import { F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'
import { useCallback, useState } from 'react'

import i18n from 'modules/core/lib/i18n'

import Launch from './Launch'
import ScheduleForLater from './ScheduleForLater'

type Mode = 'schedule' | 'launch'

interface ExistingSubmissionPeriod {
  id: string
  startDate: string
  endDate: string
}

interface Props {
  periodId: string
  companyId: string
  submissionPeriod?: ExistingSubmissionPeriod
  onClose: () => void
}

interface FormStatus {
  submit: () => void
  isSubmitting: boolean
  hasErrors: boolean
}

const SubmissionPeriodFormModal = ({ periodId, companyId, submissionPeriod, onClose }: Props) => {
  const isEditing = !!submissionPeriod
  const canLaunchNow = !submissionPeriod || new Date() < new Date(submissionPeriod.startDate)
  const [mode, setMode] = useState<Mode>('schedule')

  const [formStatus, setFormStatus] = useState<FormStatus>({
    submit: () => {},
    isSubmitting: false,
    hasErrors: false,
  })

  const handleStatusChange = useCallback((status: FormStatus) => {
    setFormStatus(status)
  }, [])

  const title = isEditing
    ? i18n.t('planning.submission_period.form.title_edit')
    : i18n.t('planning.submission_period.form.title_create')

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={title}
      primaryAction={{
        label: i18n.t('planning.submission_period.form.save'),
        onClick: formStatus.submit,
        loading: formStatus.isSubmitting,
        disabled: formStatus.hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('planning.submission_period.form.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="sm" marginBottom="md">
        <F0Text
          variant="label"
          content={i18n.t('planning.submission_period.form.schedule_title')}
        />
        <F0Text
          variant="description"
          content={i18n.t('planning.submission_period.form.schedule_description')}
        />
      </F0Box>

      {canLaunchNow && (
        <F0Box display="flex" gap="sm" marginBottom="md">
          <F0Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding="md"
            borderRadius="md"
            borderColor={mode === 'schedule' ? 'selected' : 'default'}
            background={mode === 'schedule' ? 'selected' : 'primary'}
            width="full"
            onClick={() => setMode('schedule')}
          >
            <F0Text
              variant="label"
              content={i18n.t('planning.submission_period.form.schedule_for_later')}
            />
          </F0Box>

          <F0Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding="md"
            borderRadius="md"
            borderColor={mode === 'launch' ? 'selected' : 'default'}
            background={mode === 'launch' ? 'selected' : 'primary'}
            width="full"
            onClick={() => setMode('launch')}
          >
            <F0Text
              variant="label"
              content={i18n.t('planning.submission_period.form.launch_now')}
            />
          </F0Box>
        </F0Box>
      )}

      {mode === 'schedule' ? (
        <ScheduleForLater
          periodId={periodId}
          companyId={companyId}
          submissionPeriod={submissionPeriod}
          onClose={onClose}
          onStatusChange={handleStatusChange}
        />
      ) : (
        <Launch
          periodId={periodId}
          companyId={companyId}
          submissionPeriodId={submissionPeriod?.id}
          onClose={onClose}
          onStatusChange={handleStatusChange}
        />
      )}
    </F0Dialog>
  )
}

export default SubmissionPeriodFormModal
